import { SSEEvent, createSSELineParser, parseSSEChunk } from './aiAssistantModel'
import { authHeaders, getChatBase, getAccessToken } from './chatApiBase'
import { ChatApiError, throwIfNotOk } from './chatApiError'

export async function createSession(): Promise<string> {
  const token = getAccessToken()
  const res = await fetch(`${getChatBase()}/sessions`, {
    method: 'POST',
    headers: {
      // Stamped once, at creation, so analytics can tell web sessions from
      // mobile ones. The chat API ignores this header on every other call.
      'x-synkedup-client': 'web',
      ...authHeaders(token),
    },
  })
  throwIfNotOk(res, 'Failed to create session')
  const data = await res.json()
  return data.session_id
}

export async function restoreSession(sessionId: string): Promise<void> {
  const token = getAccessToken()
  const res = await fetch(`${getChatBase()}/sessions/${sessionId}/restore`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  throwIfNotOk(res, 'Failed to restore session')
}

export async function deleteSession(sessionId: string): Promise<boolean> {
  const token = getAccessToken()
  const res = await fetch(`${getChatBase()}/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  throwIfNotOk(res, 'Failed to delete session')
  const data = await res.json()
  return data.deleted
}

export type CreateRatingBody = {
  response_text: string
  thumbs_up: boolean
  rationale: string | null
}

export async function createRating(
  sessionId: string,
  body: CreateRatingBody
): Promise<string> {
  const token = getAccessToken()
  const res = await fetch(`${getChatBase()}/sessions/${sessionId}/ratings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(body),
  })
  throwIfNotOk(res, 'Failed to create rating')
  const data = await res.json()
  return data.rating_id
}

export async function deleteRating(
  sessionId: string,
  ratingId: string
): Promise<void> {
  const token = getAccessToken()
  const res = await fetch(
    `${getChatBase()}/sessions/${sessionId}/ratings/${ratingId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    }
  )
  throwIfNotOk(res, 'Failed to delete rating')
}

async function streamSSE(
  path: string,
  body: unknown,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const token = getAccessToken()
  const response = await fetch(`${getChatBase()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // This build renders memory_saved as a card, so chat may send it. Builds
      // without the header get only the reply text (chat#256).
      'x-synkedup-capabilities': 'memory-card',
      ...authHeaders(token),
    },
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    throw new ChatApiError(
      response.status,
      `HTTP ${response.status}: ${response.statusText}`
    )
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  const parser = createSSELineParser()

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value, { stream: true })
    const events = parseSSEChunk(parser, chunk)
    for (const event of events) {
      onEvent(event)
    }
  }
}

/**
 * Post a user message and stream the reply.
 *
 * `stepOut` marks a message sent from inside a pending question set, to create
 * a record its lookup could not find. The harness keeps the panel and the
 * answers already given instead of abandoning them, so the user can come back
 * and finish. Sent only by the lookup's create action.
 */
export async function sendMessage(
  sessionId: string,
  message: string,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
  stepOut?: boolean
): Promise<void> {
  await streamSSE(
    `/sessions/${sessionId}/messages`,
    stepOut ? { message, step_out: true } : { message },
    onEvent,
    signal
  )
}

export async function sendDecision(
  sessionId: string,
  toolCallId: string,
  approved: boolean,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  await streamSSE(
    `/sessions/${sessionId}/decisions`,
    { tool_call_id: toolCallId, approved },
    onEvent,
    signal
  )
}

/**
 * Decide a whole consolidated approval card at once (chat#169).
 *
 * The same endpoint as `sendDecision`, addressed by group instead of by call.
 * A second approve after a partial failure is the retry: the server re-runs
 * only the rows still pending, so this never re-applies a row that succeeded.
 */
export async function sendGroupDecision(
  sessionId: string,
  groupId: string,
  approved: boolean,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  await streamSSE(
    `/sessions/${sessionId}/decisions`,
    { group_id: groupId, approved },
    onEvent,
    signal
  )
}

export async function submitForm(
  sessionId: string,
  formId: string,
  values: Record<string, unknown>,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  await streamSSE(
    `/sessions/${sessionId}/forms`,
    { form_id: formId, values },
    onEvent,
    signal
  )
}

export type LookupOption = {
  id: number | string
  label: string
  description?: string | null
}

export type LookupResult = {
  options: LookupOption[]
  total_count: number
}

// Identifies which pending lookup field to search: a standalone form's field,
// or a question-set form page's field. The parent value scopes a dependent
// lookup (e.g. properties for a chosen customer).
export type LookupQuery = {
  formId?: string
  questionSetId?: string
  pageId?: string
  field: string
  q: string
  parentValue?: string | number | null
}

export async function fetchLookupOptions(
  sessionId: string,
  query: LookupQuery
): Promise<LookupResult> {
  const token = getAccessToken()
  const params = new URLSearchParams({ field: query.field, q: query.q })
  if (query.formId) {
    params.set('form_id', query.formId)
  }
  if (query.questionSetId) {
    params.set('question_set_id', query.questionSetId)
  }
  if (query.pageId) {
    params.set('page_id', query.pageId)
  }
  if (query.parentValue != null && query.parentValue !== '') {
    params.set('parent_value', String(query.parentValue))
  }
  const res = await fetch(
    `${getChatBase()}/sessions/${sessionId}/lookup?${params.toString()}`,
    { headers: authHeaders(token) }
  )
  throwIfNotOk(res, 'Lookup failed')
  return res.json()
}

export async function submitQuestionSet(
  sessionId: string,
  questionSetId: string,
  answers: Record<string, unknown>,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  await streamSSE(
    `/sessions/${sessionId}/question-sets`,
    { question_set_id: questionSetId, answers },
    onEvent,
    signal
  )
}
